// QR Kiosk v2 — KV2-3 solve context strip (designer SolveReport strip). Pinned,
// collapsible (default collapsed — v1 accordion parity). Binds number / title /
// openDate (+part) collapsed and description expanded. #number is the REAL value
// verbatim (PG-6 discipline). Tokens only; dates formatted via REPORT_DATE_FORMAT.
import React, { useState } from "react";
import moment from "moment";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ServiceReport } from "../../models/serviceReportsModels";
import { Glyph, GD } from "../shared/glyphs";
import { REPORT_DATE_FORMAT } from "./reportUtils";
import { localizeDateFormat } from "../shared/dateFormat";

const EM_DASH = "—";

export interface SolveContextStripProps {
  report: ServiceReport;
  partName?: string;
}

export function SolveContextStrip({ report, partName }: SolveContextStripProps) {
  const { translate, i18n } = useTranslation("public");
  const [open, setOpen] = useState(false);

  const openedDate = report.openDate ? moment(report.openDate).format(localizeDateFormat(REPORT_DATE_FORMAT, i18n.language)) : EM_DASH;

  return (
    <div
      className="kv2-enter"
      style={{
        margin: "10px 16px 0",
        borderRadius: 11,
        border: "1px solid var(--border)",
        background: "var(--bg-soft)",
        padding: "10px 13px",
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{ width: "100%", border: "none", background: "transparent", padding: 0, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, fontWeight: 600, color: "var(--tier-accent)", flexShrink: 0 }}>
            {`#${report.number}`}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--ink-2)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {report.title}
          </span>
          <span
            style={{
              color: "var(--muted)",
              display: "inline-flex",
              flexShrink: 0,
              transform: open ? "rotate(270deg)" : "rotate(90deg)",
              transition: "transform .15s",
            }}
          >
            <Glyph d={GD.chev} size={12} />
          </span>
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)", marginTop: 3 }}>
          {translate("kioskV2OpenedAt", { date: openedDate })}
          {partName ? ` · ${partName}` : ""}
        </div>
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          <div className="eyebrow" style={{ color: "var(--faint)", marginBottom: 4 }}>
            {translate("reportDescription")}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>{report.description}</div>
        </div>
      )}
    </div>
  );
}

export default SolveContextStrip;
